// NAV Calculation Engine
// Implements all formulas from the Loan Agreement

import { differenceInDays } from 'date-fns';
import { PerformanceFeeCalculation, Series } from '../types';

/**
 * Calculate Gross Profit for a series
 * Formula: End NAV - Start NAV
 */
export const calculateGrossProfit = (
  startNAV: number,
  endNAV: number
): number => {
  return endNAV - startNAV;
};

/**
 * Calculate Annualised Return Rate
 * Formula: ((End NAV - Start NAV) / Start NAV) * (365 / days in period)
 */
export const calculateAnnualisedReturnRate = (
  startNAV: number,
  endNAV: number,
  startDate: Date,
  endDate: Date
): number => {
  if (startNAV === 0) return 0;
  
  const daysInPeriod = differenceInDays(endDate, startDate);
  if (daysInPeriod === 0) return 0;
  
  const returnRate = (endNAV - startNAV) / startNAV;
  const annualisedRate = returnRate * (365 / daysInPeriod);
  
  return annualisedRate;
};

/**
 * Pro-rate Hurdle Rate for periods less than a year
 * Formula: Hurdle Rate * (days in period / 365)
 */
export const calculateProratedHurdleRate = (
  annualHurdleRate: number,
  startDate: Date,
  endDate: Date
): number => {
  const daysInPeriod = differenceInDays(endDate, startDate);
  return annualHurdleRate * (daysInPeriod / 365);
};

/**
 * Main Performance Fee Calculation Engine
 * Implements all scenarios from Clause 3.2 of the Agreement
 */
export const calculatePerformanceFee = (
  seriesId: string,
  startNAV: number,
  endNAV: number,
  highWaterMark: number,
  startDate: Date,
  endDate: Date,
  performanceFeeRate: number = 0.20, // 20%
  annualHurdleRate: number = 0.03 // 3%
): PerformanceFeeCalculation => {
  // Step 1: Calculate Gross Profit
  const grossProfit = calculateGrossProfit(startNAV, endNAV);
  
  // Step 2: Calculate Annualised Return Rate
  const annualisedReturnRate = calculateAnnualisedReturnRate(
    startNAV,
    endNAV,
    startDate,
    endDate
  );
  
  // Step 3: Determine if Hurdle Rate is met
  const hurdleRateMet = annualisedReturnRate > annualHurdleRate;
  
  // Step 4: Determine if High Water Mark is exceeded
  const highWaterMarkExceeded = endNAV > highWaterMark;
  
  // Step 5: Apply Performance Fee Logic based on scenarios
  let performanceFee = 0;
  let interest = 0;
  let scenario: 'loss' | 'below_hurdle' | 'underwater_recovery' | 'full_performance_fee';
  let performanceFeeApplicable = false;
  
  // Scenario 1: Loss (Gross Profit ≤ 0)
  if (grossProfit <= 0) {
    scenario = 'loss';
    interest = grossProfit; // Lender bears full loss
    performanceFee = 0;
    performanceFeeApplicable = false;
  }
  // Scenario 2: Positive Return Below Hurdle Rate
  else if (grossProfit > 0 && !hurdleRateMet) {
    scenario = 'below_hurdle';
    interest = grossProfit;
    performanceFee = 0;
    performanceFeeApplicable = false;
  }
  // Scenario 3: Underwater Recovery (Return exceeds hurdle but still below HWM)
  else if (grossProfit > 0 && hurdleRateMet && !highWaterMarkExceeded) {
    scenario = 'underwater_recovery';
    interest = grossProfit;
    performanceFee = 0;
    performanceFeeApplicable = false;
  }
  // Scenario 4: Full Performance Fee Applicable
  else if (grossProfit > 0 && hurdleRateMet && highWaterMarkExceeded) {
    scenario = 'full_performance_fee';
    performanceFee = grossProfit * performanceFeeRate;
    interest = grossProfit - performanceFee;
    performanceFeeApplicable = true;
  }
  // Fallback (should not reach here)
  else {
    scenario = 'loss';
    interest = grossProfit;
    performanceFee = 0;
    performanceFeeApplicable = false;
  }
  
  return {
    seriesId,
    grossProfit,
    annualisedReturnRate,
    hurdleRate: annualHurdleRate,
    hurdleRateMet,
    endNAV,
    highWaterMark,
    highWaterMarkExceeded,
    performanceFeeApplicable,
    performanceFee,
    interest,
    scenario
  };
};

/**
 * Calculate Redemption Value
 * Formula from Clause 4.4: NAV - Performance Fee - Early Redemption Fee (if applicable)
 */
export const calculateRedemptionValue = (
  seriesNav: number,
  performanceFee: number,
  isEarlyRedemption: boolean,
  earlyRedemptionFeeRate: number = 0.03 // 3%
): number => {
  // Deduct performance fee first
  let redemptionValue = seriesNav - performanceFee;
  
  // Deduct early redemption fee if applicable
  if (isEarlyRedemption) {
    const earlyRedemptionFee = redemptionValue * earlyRedemptionFeeRate;
    redemptionValue = redemptionValue - earlyRedemptionFee;
  }
  
  // Ensure redemption value is not negative
  return Math.max(0, redemptionValue);
};

/**
 * Check if a series is still in lock-up period
 */
export const isInLockupPeriod = (
  issueDate: Date,
  lockupPeriodMonths: number = 12,
  currentDate: Date = new Date()
): boolean => {
  const lockupEnd = new Date(issueDate);
  lockupEnd.setMonth(lockupEnd.getMonth() + lockupPeriodMonths);
  
  return currentDate < lockupEnd;
};

/**
 * Calculate a Series' proportionate share of the total NAV
 * Formula: (Principal Amount / Total Principal) * Total NAV
 */
export const calculateSeriesProportionateNAV = (
  seriesPrincipalAmount: number,
  totalPrincipalAllSeries: number,
  totalPoolNAV: number
): number => {
  if (totalPrincipalAllSeries === 0) return 0;
  
  const proportion = seriesPrincipalAmount / totalPrincipalAllSeries;
  return proportion * totalPoolNAV;
};

/**
 * Update High Water Mark after crystallisation
 * Only resets if performance fee was charged (meaning all conditions were met)
 */
export const updateHighWaterMark = (
  currentHighWaterMark: number,
  endNAV: number,
  performanceFeeWasCharged: boolean
): number => {
  if (performanceFeeWasCharged && endNAV > currentHighWaterMark) {
    return endNAV;
  }
  return currentHighWaterMark;
};

/**
 * Calculate total Performance Fees collected in a period
 */
export const calculateTotalPerformanceFees = (
  calculations: PerformanceFeeCalculation[]
): number => {
  return calculations.reduce((total, calc) => total + calc.performanceFee, 0);
};

/**
 * Calculate portfolio return percentage
 */
export const calculateReturnPercentage = (
  startValue: number,
  endValue: number
): number => {
  if (startValue === 0) return 0;
  return ((endValue - startValue) / startValue) * 100;
};

/**
 * Calculate Year-to-Date return
 */
export const calculateYTDReturn = (
  startOfYearNAV: number,
  currentNAV: number
): number => {
  return calculateReturnPercentage(startOfYearNAV, currentNAV);
};

/**
 * Calculate time-weighted return (useful for performance reporting)
 */
export const calculateTimeWeightedReturn = (
  cashFlows: { date: Date; amount: number; nav: number }[]
): number => {
  if (cashFlows.length < 2) return 0;
  
  let cumulativeReturn = 1;
  
  for (let i = 1; i < cashFlows.length; i++) {
    const previousNAV = cashFlows[i - 1].nav;
    const currentNAV = cashFlows[i].nav - cashFlows[i].amount;
    
    if (previousNAV !== 0) {
      const periodReturn = currentNAV / previousNAV;
      cumulativeReturn *= periodReturn;
    }
  }
  
  return (cumulativeReturn - 1) * 100;
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format percentage for display
 */
export const formatPercentage = (rate: number, decimals: number = 2): string => {
  return `${(rate * 100).toFixed(decimals)}%`;
};

/**
 * Validate NAV calculation inputs
 */
export const validateCalculationInputs = (
  startNAV: number,
  endNAV: number,
  highWaterMark: number
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (startNAV < 0) {
    errors.push('Start NAV cannot be negative');
  }
  
  if (endNAV < 0) {
    errors.push('End NAV cannot be negative');
  }
  
  if (highWaterMark < 0) {
    errors.push('High Water Mark cannot be negative');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Helper to get next crystallisation date (December 31st)
 */
export const getNextCrystallisationDate = (fromDate: Date = new Date()): Date => {
  const year = fromDate.getFullYear();
  const thisYearCrystallisation = new Date(year, 11, 31); // December 31st
  
  if (fromDate <= thisYearCrystallisation) {
    return thisYearCrystallisation;
  } else {
    return new Date(year + 1, 11, 31);
  }
};

/**
 * Calculate days until next crystallisation
 */
export const getDaysUntilNextCrystallisation = (fromDate: Date = new Date()): number => {
  const nextCrystallisation = getNextCrystallisationDate(fromDate);
  return differenceInDays(nextCrystallisation, fromDate);
};

/**
 * Calculate Redemption Date (Request Date + 30 days)
 */
export const calculateRedemptionDate = (requestDate: Date, noticeDays: number = 30): Date => {
  const redemptionDate = new Date(requestDate);
  redemptionDate.setDate(redemptionDate.getDate() + noticeDays);
  return redemptionDate;
};
